import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { Loader, Alert, Stat, Empty, ProgressBar, relativeTime } from '../../components/ui.jsx';
import { useSchoolScope } from './SchoolScope.jsx';

export default function AdminHome() {
  const { user } = useAuth();
  const { schoolId, picker } = useSchoolScope();

  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user.role === 'superadmin' && !schoolId) {
      setData(null);
      return;
    }
    setData(null);
    setError('');
    api.adminOverview(schoolId).then(setData).catch((err) => setError(err.message));
  }, [schoolId, user.role]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>🏫 School dashboard</h1>
          <p>
            How your students are progressing through CyberCub. Numbers refresh every time you open
            this page.
          </p>
        </div>
        {picker}
      </div>

      {error && <Alert kind="bad">{error}</Alert>}

      {!schoolId && user.role === 'superadmin' ? (
        <Empty icon="🏫" title="Pick a school" text="Choose a school above to see its student data." />
      ) : !data ? (
        <Loader label="Crunching the numbers…" />
      ) : (
        <>
          <div className="school-banner">
            <div>
              <h2 style={{ margin: 0 }}>{data.school.name}</h2>
              <p className="muted small" style={{ margin: 0 }}>
                Code <strong>{data.school.code}</strong>
                {data.school.city ? ` · ${data.school.city}` : ''}
                {data.school.state ? `, ${data.school.state}` : ''}
              </p>
            </div>
            <span className={`pill ${data.school.isActive ? 'pill-good' : 'pill-bad'}`}>
              {data.school.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div className="stat-grid mt-md">
            <Stat icon="🎒" tone="grape" value={data.totals.students} label="Students enrolled" />
            <Stat icon="🟢" tone="mint" value={data.totals.activeThisWeek} label="Active this week" />
            <Stat icon="✅" tone="sky" value={data.totals.lessonsCompleted} label="Lessons completed" />
            <Stat icon="🎯" tone="sun" value={data.totals.avgScore || '—'} label="Average quiz score" />
          </div>

          <div className="panel-grid mt-lg">
            {/* ---- per lesson ---- */}
            <section className="card-flat">
              <h2>Progress by lesson</h2>
              {data.totals.students === 0 ? (
                <p className="faint">No students yet.</p>
              ) : (
                <div className="lesson-bars">
                  {data.byLesson.map((l) => (
                    <div key={l.slug} className="lesson-bar">
                      <div className="row spread gap-sm">
                        <span className="lesson-bar-title">
                          <span aria-hidden="true">{l.icon}</span> {l.title}
                        </span>
                        <span className="small muted">
                          {l.completed}/{data.totals.students} done
                          {Number(l.avgScore) > 0 && ` · avg ${l.avgScore}`}
                        </span>
                      </div>
                      <ProgressBar
                        value={Number(l.completed)}
                        max={data.totals.students}
                        tone="mint"
                        label={false}
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ---- by grade ---- */}
            <section className="card-flat">
              <h2>By grade</h2>
              {data.byGrade.length === 0 ? (
                <p className="faint">No students yet.</p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Grade</th>
                      <th className="num">Students</th>
                      <th className="num">Lessons done</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byGrade.map((g) => (
                      <tr key={g.grade}>
                        <td><strong>{g.grade}</strong></td>
                        <td className="num">{g.students}</td>
                        <td className="num">{g.lessonsCompleted}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h2 className="mt-lg">Recent sign-ins</h2>
              {data.recentLogins.length === 0 ? (
                <p className="faint">Nobody has signed in yet.</p>
              ) : (
                <ul className="login-history">
                  {data.recentLogins.map((l, i) => (
                    <li key={i}>
                      <span>{l.full_name} <span className="faint small">({l.user_id})</span></span>
                      <span className="muted small">{relativeTime(l.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <div className="mt-lg">
            <Link to="/admin/students" className="btn">
              See the full student list →
            </Link>
          </div>
        </>
      )}
    </>
  );
}
